/**
 * Análises (D522): how a BI figure is written. Every figure keeps its canonical unit on the wire (integer minor units + ISO
 * currency, meters, seconds, a 6-decimal fraction for a ratio) and is rendered here for the chrome language through
 * `@vello/format` — never a hardcoded currency, separator or unit. The language is the console's; the currency is the figure's own.
 */
import { type FormatContext, currencyMinorUnits, formatDistance, formatDuration, formatMoney } from '@vello/format';
import { BUCKET_EDGES, type BucketDimensionId, type ResultCell, bucketRanges } from '@vello/analytics-semantic';
import { type BiUnit, type Delta, cellMinor, cellNumber } from './bi-core.js';

export interface BiFormatter {
  readonly count: (n: number) => string;
  readonly percent: (fraction: number) => string;
  /** A figure as text: `undefined` cells and ratios over nothing read as the caller's "unavailable". */
  readonly cell: (unit: BiUnit, cell: ResultCell | undefined, currency: string | undefined) => string | undefined;
  /** A figure as the number a chart draws (major units for money, the fraction for a ratio). */
  readonly chartValue: (unit: BiUnit, cell: ResultCell, currency: string | undefined) => number;
  /** The text of a chart value of this unit (a tooltip or axis tick). */
  readonly chartText: (unit: BiUnit, value: number, currency: string | undefined) => string;
  /** `+12,5%`, `-3,0 p.p.` — the sign is always written, so the colour is never the only signal. */
  readonly delta: (delta: Delta, ppUnit: string) => string | undefined;
}

export function biFormatter(lang: string): BiFormatter {
  const ctx: FormatContext = { locale: lang, timeZone: 'UTC', measurement: 'metric' };
  const nf = new Intl.NumberFormat(lang);
  const pf = new Intl.NumberFormat(lang, { style: 'percent', maximumFractionDigits: 1 });
  const signed = new Intl.NumberFormat(lang, { maximumFractionDigits: 1, minimumFractionDigits: 1, signDisplay: 'exceptZero' });
  const signedPct = new Intl.NumberFormat(lang, { maximumFractionDigits: 1, minimumFractionDigits: 1, signDisplay: 'exceptZero' });
  const df = new Intl.NumberFormat(lang, { maximumFractionDigits: 2 });
  /** A signed duration: `+1 min 20 s` late, `-45 s` early, `0 s` on the dot - the sign is always written. */
  const signedDuration = (seconds: number): string => {
    const rounded = Math.round(seconds);
    if (rounded === 0) return formatDuration(0, ctx);
    return `${rounded < 0 ? '-' : '+'}${formatDuration(Math.abs(rounded), ctx)}`;
  };
  const exponent = (currency: string | undefined): number => {
    if (currency === undefined) return 2;
    try {
      return currencyMinorUnits(currency);
    } catch {
      return 2;
    }
  };
  const money = (minor: bigint, currency: string): string => {
    try {
      return formatMoney({ amountMinor: minor, currency }, ctx);
    } catch {
      return `${currency} ${nf.format(Number(minor))}`;
    }
  };
  const chartValue: BiFormatter['chartValue'] = (unit, cell, currency) => {
    if (unit === 'money') {
      const minor = cellMinor(cell);
      return minor === undefined ? 0 : Number(minor) / 10 ** exponent(currency);
    }
    return cellNumber(cell) ?? 0;
  };
  return {
    count: (n) => nf.format(n),
    percent: (f) => pf.format(f),
    cell: (unit, cell, currency) => {
      if (cell === undefined || cell.value === null) return undefined;
      switch (unit) {
        case 'money': {
          const minor = cellMinor(cell);
          return minor === undefined ? undefined : money(minor, currency ?? 'XXX');
        }
        case 'ratio': {
          const n = cellNumber(cell);
          return n === undefined ? undefined : pf.format(n);
        }
        case 'duration_s': {
          const n = cellNumber(cell);
          return n === undefined || n < 0 ? undefined : formatDuration(n, ctx);
        }
        case 'distance_m': {
          const n = cellNumber(cell);
          return n === undefined ? undefined : formatDistance(n, ctx);
        }
        case 'delta_s': {
          const n = cellNumber(cell);
          return n === undefined ? undefined : signedDuration(n);
        }
        case 'decimal': {
          const n = cellNumber(cell);
          return n === undefined ? undefined : df.format(n);
        }
        default: {
          const n = cellNumber(cell);
          return n === undefined ? undefined : nf.format(n);
        }
      }
    },
    chartValue,
    chartText: (unit, value, currency) => {
      switch (unit) {
        case 'money':
          return money(BigInt(Math.round(value * 10 ** exponent(currency))), currency ?? 'XXX');
        case 'ratio':
          return pf.format(value);
        case 'duration_s':
          return formatDuration(Math.max(0, value), ctx);
        case 'distance_m':
          return formatDistance(value, ctx);
        case 'delta_s':
          return signedDuration(value);
        case 'decimal':
          return df.format(value);
        default:
          return nf.format(value);
      }
    },
    delta: (d, ppUnit) => {
      if (d.kind === 'none' || d.kind === 'new') return undefined;
      if (d.kind === 'flat') return `${signed.format(0)}%`;
      return d.kind === 'pp' ? `${signed.format(d.value)} ${ppUnit}` : `${signedPct.format(d.value)}%`;
    },
  };
}

/** Day-of-week names for the weekday keys `1`…`7` (Monday first, the planner's ISO numbering). */
export function weekdayName(lang: string, iso: number, style: 'long' | 'short' = 'long'): string {
  // 2024-01-01 is a Monday.
  return new Intl.DateTimeFormat(lang, { weekday: style, timeZone: 'UTC' }).format(new Date(Date.UTC(2024, 0, iso)));
}

/**
 * The text of a dimension key by what kind of key it is. Time keys use `Intl` for the chrome language; a code goes through
 * `codeLabel` (the catalog label of the code, from `@vello/vocabulary` — never the code); a reference shows its label when the server sent one.
 */
export function dimensionValueText(
  valueType: string,
  key: string | null | undefined,
  label: string | null | undefined,
  lang: string,
  codeLabel: (code: string) => string,
  emptyLabel: string,
  short = false,
): string {
  if (key === null || key === undefined || key === '') return emptyLabel;
  switch (valueType) {
    case 'day':
      return /^\d{4}-\d{2}-\d{2}$/.test(key) ? new Intl.DateTimeFormat(lang, short ? { day: '2-digit', month: 'short', timeZone: 'UTC' } : { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${key}T12:00:00Z`)) : key;
    case 'month':
      return /^\d{4}-\d{2}$/.test(key) ? new Intl.DateTimeFormat(lang, { month: 'short', year: 'numeric', timeZone: 'UTC' }).format(new Date(`${key}-01T12:00:00Z`)) : key;
    case 'hour':
      return `${key.replace('#2', '')}:00${key.endsWith('#2') ? ' (2)' : ''}`;
    case 'weekday':
      return /^[1-7]$/.test(key) ? weekdayName(lang, Number(key)) : key;
    case 'dayPart':
    case 'vertical':
    case 'code':
      return codeLabel(key);
    case 'market':
    case 'country':
    case 'region':
    case 'currency':
    case 'year':
    case 'quarter':
      return label ?? key;
    default:
      return label !== null && label !== undefined && label !== '' ? label : key;
  }
}

// ───────────────────────────── D531: the text of a bucket ─────────────────────────────

const isBucketDimension = (id: string): id is BucketDimensionId => Object.prototype.hasOwnProperty.call(BUCKET_EDGES, id);

/**
 * The text of the key of a bucket dimension (`shift`, `match_time_bucket`, `pickup_time_bucket`, `eta_deviation_bucket`): `08-10 h` for a two-hour shift,
 * `30 s - 45 s` for a duration range, `< 15 s` / `>= 300 s` at the open ends. Undefined for any other dimension (the caller falls back to the plain key).
 * `duration` and `signed` are the console's own duration formats, so the words follow the language.
 */
export function bucketText(dimension: string, key: string | null | undefined, fmt: { readonly duration: (seconds: number) => string; readonly signed: (seconds: number) => string }): string | undefined {
  if (key === null || key === undefined || !isBucketDimension(dimension)) return undefined;
  const range = bucketRanges(BUCKET_EDGES[dimension]).find((b) => b.key === key);
  if (range === undefined) return undefined;
  const hour = (h: number): string => String(h).padStart(2, '0');
  if (dimension === 'shift') return `${hour(range.from ?? 0)}\u2013${hour(range.to ?? 24)} h`;
  const f = dimension === 'eta_deviation_bucket' ? fmt.signed : fmt.duration;
  if (range.from === null) return `< ${f(range.to as number)}`;
  if (range.to === null) return `\u2265 ${f(range.from)}`;
  return `${f(range.from)} \u2013 ${f(range.to)}`;
}
